import { useCallback, useMemo } from 'react';

import { useAnalyticsContext } from '@/utils/analytics/AnalyticsContext';
import { useCurrentUser } from '@/stores/useCurrentUser';
import { showSmbInvite } from '@/utils/commonUtils';
import useRenterInviteDrawerStore from '@/stores/useRenterInviteDrawerStore';
import type { InviteSegment } from '@/features/RenterInvite/RenterInvite.types';
import { useInviteTrackingContext } from '../context';

export function useInviteTracking() {
  const { track } = useAnalyticsContext();
  const { user } = useCurrentUser();

  const context = useInviteTrackingContext();
  const invitePageFlowId = context?.flowId;
  const invitePageEntryPoint = context?.entryPoint;
  const { flowId: drawerFlowId, entryPoint: drawerEntryPoint } = useRenterInviteDrawerStore();
  const flowId = invitePageFlowId ?? drawerFlowId;
  const entryPoint = invitePageEntryPoint ?? drawerEntryPoint;
  const inviteFormVariant = context ? 'renter_invite_page' : 'app_tracker_drawer';

  const inviteSegment: InviteSegment = useMemo(
    () => (user && showSmbInvite(user) ? 'smb' : 'institutional'),
    [user],
  );

  const trackInvite = useCallback(
    (eventName: string, properties: Record<string, unknown> = {}) => {
      track(eventName, {
        flow_id: flowId,
        user_id: user?.id,
        company_id: user?.company?.operator_company_id,
        invite_segment: inviteSegment,
        invite_form_variant: inviteFormVariant,
        ...properties,
      });
    },
    [track, flowId, user, inviteSegment, inviteFormVariant],
  );

  return { trackInvite, entryPoint, inviteSegment, flowId };
}
